import { computed, Directive, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { WidgetParams } from '@common/types/content.type';
import { switchMap, take } from 'rxjs/operators';

import type { WidgetErrorItem } from '@common/types/widget-errors.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import type { DenormalizedColumnEntity, WebWidgetModel, WebLegacyWidgetEntity } from '@web/types';

import { AddWidgetOrGridComponent } from '@web/layout/content/page-content/components/add-widget-or-grid';
import { AddWidgetComponent } from '@web/layout/content/page-content/components/add-widget';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { SpecialBlocksService } from '@web/core/services/special-blocks.service';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { Widgets } from '@common/enums';
import { widgetNameOf, widgetParentIdOf } from '@common/helpers/widget-envelope';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';

@Directive({})
export abstract class BaseWidgetComponent {
    public readonly widget = input.required<WebLegacyWidgetEntity>();
    public readonly widgetErrors = input<WidgetErrorItem>();
    public readonly index = input<number>();
    public readonly blockId = input<string>();
    public readonly column = input<DenormalizedColumnEntity>();
    public readonly gridId = input<string>();

    public readonly isTablet = input<boolean>(false);
    public readonly isMobile = input<boolean>(false);

    protected readonly modalService: BsModalService = inject(BsModalService);
    protected readonly tabsFacade: TabsFacade = inject(TabsFacade);
    protected readonly gridsFacade: GridsFacade = inject(GridsFacade);
    protected readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    protected readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    protected readonly appFacade: AppFacade = inject(AppFacade);
    protected readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    protected readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    protected readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    protected readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    protected readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    protected readonly blocks$ = this.blocksFacade.blocks$;

    public isSpecific: boolean = false;
    public readonly isShowMoreActionState = signal(false);

    /** The widget kind, from either envelope (legacy `name`, versioned `widget:<kind>`). */
    public readonly widgetName = computed<Widgets>(() => widgetNameOf(this.widget()));
    public readonly extendedWidgetParams = computed<WidgetParams>(() =>
        this.widgetsService.getExtendedWidgetParams(this.widgetName())
    );

    private readonly entityId = toSignal(this.layoutFacade.entityId$);
    public readonly isWidgetSelected = computed<boolean>(() => this.entityId() === this.widget().id);
    public readonly hideControls = toSignal(this.layoutFacade.hideControls$, { initialValue: false });
    protected readonly isHovered = signal(false);
    private readonly isDragInProgress = signal(false);

    /**
     * The controls are rendered only while in use. Besides hover that is the selected widget, an open
     * "more" menu (the pointer leaves for it) and a drag — CDK moves the widget away from the pointer,
     * and the drag handle must not be destroyed mid-drag.
     */
    public readonly controlsVisible = computed<boolean>(
        () =>
            this.hideControls() &&
            (this.isHovered() || this.isWidgetSelected() || this.isShowMoreActionState() || this.isDragInProgress())
    );

    /** Legacy `data.hideOn*` flags — a versioned widget carries none. */
    private readonly hiddenOn = computed(() => {
        const data = this.widget().data as { hideOnMobile?: boolean; hideOnTablet?: boolean; hideOnDesktop?: boolean };

        return {
            mobile: !!data?.hideOnMobile,
            tablet: !!data?.hideOnTablet,
            desktop: !!data?.hideOnDesktop,
        };
    });

    public readonly isHidden = computed<boolean>(() => {
        const { mobile, tablet, desktop } = this.hiddenOn();
        return mobile || tablet || desktop;
    });

    public readonly hiddenIconPath = computed<string>(() => {
        const { mobile, tablet, desktop } = this.hiddenOn();
        const count = Number(mobile) + Number(tablet) + Number(desktop);

        if (count > 1) {
            return './assets/img/websites/lpc-hidden-all-label-icon.svg';
        }
        if (mobile) {
            return './assets/img/websites/lpc-hidden-mobile-label-icon.svg';
        }
        if (tablet) {
            return './assets/img/websites/lpc-hidden-tablet-label-icon.svg';
        }
        if (desktop) {
            return './assets/img/websites/lpc-hidden-desktop-label-icon.svg';
        }

        return '';
    });

    public readonly hiddenIconTooltip = computed<string>(() => {
        const { mobile, tablet, desktop } = this.hiddenOn();

        if (mobile && tablet && desktop) {
            return 'control_widget_hidden_to_all';
        }
        if (mobile && tablet) {
            return 'control_widget_hidden_mobile_tablet';
        }
        if (mobile && desktop) {
            return 'control_widget_hidden_mobile_desktop';
        }
        if (tablet && desktop) {
            return 'control_widget_hidden_tablet_desktop';
        }
        if (mobile) {
            return 'control_widget_hidden_mobile';
        }
        if (tablet) {
            return 'control_widget_hidden_tablet';
        }
        if (desktop) {
            return 'control_widget_hidden_desktop';
        }

        return '';
    });

    public readonly isCrmProductWidgetRestricted = toSignal(
        toObservable(this.widget).pipe(switchMap((widget) => this.productRestrictionFacade.widgetRestricted$(widget))),
        { initialValue: false }
    );

    public addWidget(blockHasLogo?): void {
        this.productRestrictionFacade
            .widgetRestricted$(this.widget())
            .pipe(take(1))
            .subscribe((isRestricted) => {
                if (isRestricted) return;

                this.showAddWidgetModal(blockHasLogo);
            });
    }

    private showAddWidgetModal(blockHasLogo?): void {
        if (this.isSpecific) {
            const modalRef: BsModalRef<AddWidgetComponent> = this.modalService.show(AddWidgetComponent, {
                backdrop: false,
                class: 'add-widgets-modal',
                initialState: {
                    widgetType: this.isSpecific ? 'specific' : 'default',
                    isDisabledLogoWidget: blockHasLogo,
                },
            });
            modalRef.content.onClose.subscribe((widget: WebWidgetModel) => {
                if (!widget) {
                    return;
                }

                this.widgetsFacade.createWidget(widgetParentIdOf(this.widget()), this.index() + 1, widget.name);
            });
            return;
        }

        const modalRef: BsModalRef<AddWidgetOrGridComponent> = this.modalService.show(AddWidgetOrGridComponent, {
            backdrop: false,
            class: 'add-widgets-modal',
            initialState: {
                isSpecific: this.isSpecific,
                isDisabledLogoWidget: blockHasLogo,
            },
        });

        modalRef.content.onClose.subscribe((payload) => {
            if (!payload) {
                return;
            }

            if (payload.type === 'tab') {
                this.tabsFacade.create(this.column().id, this.index() + 1, { variant: payload.variant });
                return;
            }

            if (payload.type === 'grid') {
                this.gridsFacade.create(this.column().id, payload.variant, this.index() + 1);
                return;
            }

            if (!payload.entity) {
                return;
            }

            this.widgetsFacade.createWidget(this.column().id, this.index() + 1, payload.entity.name);
            this.animationHandler(payload.entity);
        });
    }

    public copyWidget(): void {
        this.widgetsFacade.copyWidget(this.widget(), this.index());
    }

    public removeWidget(): void {
        this.widgetsFacade.removeWidget(this.widget(), this.index());
    }

    public selectWidget(): void {
        if (this.widgetName() === Widgets.SHOP_CART_ICON) {
            this.blocks$.pipe(take(1)).subscribe((blocks) =>
                this.layoutFacade.setSelectedEntity({
                    entityType: ESelectedEntityType.specificBlock,
                    entityId: blocks[0].id,
                    entityStep: 'cart_icon',
                })
            );
            return;
        }

        const widgetId = this.widget().id;
        const column = this.column();

        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.widget, entityId: widgetId });

        this.navDrawerService.selectInNavigation({
            entityId: widgetId,
            blockId: this.blockId(),
            sectionId: column?.parentId,
            columnId: column?.id,
            gridId: this.gridId(),
        });
    }

    public setMoreActionState(state: boolean): void {
        this.isShowMoreActionState.set(state);
    }

    public dragStarted(): void {
        this.isDragInProgress.set(true);
        this.layoutFacade.setIsEntityDragging(true);
    }

    public dragEnded(): void {
        this.isDragInProgress.set(false);
        this.layoutFacade.setIsEntityDragging(false);
    }

    /** A product card brings its own entrance animation, so the column drops its. */
    private animationHandler(entity): void {
        if (entity.name !== Widgets.PRODUCT_CARD && entity.name !== Widgets.CATALOG_PRODUCTS) {
            return;
        }

        const column = this.column();

        // Only an ordinary column can be animated at all.
        if (column?.entitySubType !== 'default') {
            return;
        }

        const { props } = column;

        this.columnsFacade.updateColumnProperties(widgetParentIdOf(this.widget()), {
            props: { ...props, animation: { ...props.animation, isActive: false } },
        });
    }
}
